import React, { useState } from 'react';
import { useNavigate, NavLink } from "react-router-dom";
import { auth } from "../../firebase/config"; // Ruta idéntica a la que usás en Login.jsx
import { sendPasswordResetEmail } from "firebase/auth";

/**
 * ResetPassword - Componente para recuperar accesos mediante correo de Firebase.
 * Permite que los colaboradores generen de forma segura su clave de producción.
 */
const ResetPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleReset = async (e) => {
        e.preventDefault();
        
        if (!email) {
            setError("Por favor, ingrese su correo electrónico.");
            return;
        }

        setError(null);
        setSuccess(false);
        setLoading(true);

        const cleanEmail = email.trim().toLowerCase();

        try {
            // Envía el correo automático de recuperación de Firebase Auth
            await sendPasswordResetEmail(auth, cleanEmail);
            setSuccess(true);
        } catch (error) {
            console.error("Reset Password Security Error:", error.code);
            
            // Unificamos mensajes para proteger la privacidad de las cuentas
            const errorMap = {
                'auth/invalid-email': "El correo ingresado no es válido.",
                'auth/user-not-found': "No existe ningún colaborador registrado con ese correo.",
                'auth/too-many-requests': "Demasiadas solicitudes. Por favor, intente de nuevo más tarde."
            };

            setError(errorMap[error.code] || "Ocurrió un error al procesar la solicitud. Verifique su conexión.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans text-left">
            <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
                <img src="/imagenes/Logo.svg" alt="Logo IB Brokers" className="mx-auto h-16 w-auto" />
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900 tracking-tight">
                    Recuperar Contraseña
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                    Le enviaremos un correo con un enlace seguro para restablecer su clave de acceso.
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-2xl sm:px-10 border border-gray-100">
                    {success ? (
                        <div className="space-y-6 text-center animate-fade-in">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div className="text-sm text-slate-600">
                                <p className="font-bold text-slate-800 text-base mb-2">¡Enlace enviado con éxito!</p>
                                <p className="mb-4">Hemos enviado un email a <span className="font-bold text-indigo-600">{email}</span> con las instrucciones.</p>
                                <p className="text-xs text-gray-400 italic">Si no lo ve en unos minutos, revise su carpeta de Spam o Correo no deseado.</p>
                            </div>
                            <div className="pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => navigate("/")}
                                    className="w-full flex justify-center py-2.5 px-4 rounded-xl border border-transparent shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition active:scale-95 cursor-pointer"
                                >
                                    Ir al Login
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleReset} className="space-y-6" noValidate>
                            <div>
                                <label htmlFor="email" className="block text-sm font-bold text-gray-600 mb-1">
                                    Email de Colaborador
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="ejemplo@segurosib.com"
                                        className={`appearance-none block w-full px-4 py-2.5 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${error ? 'border-red-300 bg-red-50/10' : 'border-gray-300'}`}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded text-xs text-red-700 font-medium animate-fade-in">
                                    {error}
                                </div>
                            )}

                            <div className="flex flex-col gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition active:scale-95 disabled:bg-indigo-300 cursor-pointer"
                                >
                                    {loading ? (
                                        <div className="flex items-center gap-2">
                                            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enviando...
                                        </div>
                                    ) : "Enviar Enlace"}
                                </button>

                                <NavLink 
                                    to="/" 
                                    className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-xl shadow-sm text-sm font-bold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition active:scale-95 text-center"
                                >
                                    Cancelar
                                </NavLink>
                            </div>
                        </form>
                    )}
                </div>
            </div>
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default ResetPassword;